import React, { Fragment, useCallback, useEffect, useMemo } from 'react'
import { Button, Card, Col, Descriptions, Dropdown, message, Row, Space, Spin, Table } from 'antd'
import { useParams } from 'react-router-dom'
import * as Icons from '@ant-design/icons'
import { useBackgroundNavigate } from '@shared/hooks'

import { PageContent } from '@shared/styled/page-content'

import { useStatusList } from '../../status/hooks/use-list'
import { useAppointmentProducts, useAppointmentDetails } from '../hooks'
import { localURLMaker, urlMaker } from '@shared/utils/url-maker'
import { useAuth } from '../../me/hooks'
import { AppointmentRoutes } from '../constants'
import { useSearchParams } from '@shared/hooks'
import { StopPropagation } from '@shared/components/stop-propagation'
import { AppointmentProduct } from '../types'

export const AppointmentDetails = () => {
  const navigate = useBackgroundNavigate()
  const { id = '' } = useParams<{ id: string }>()
  const { can } = useAuth()

  const statusList = useStatusList({ modelId: 13 })
  const { data, loading, reFetch, edit, remove, print, updateStatus } = useAppointmentDetails(id)
  const { data: products, reFetch: reFetchProducts } = useAppointmentProducts(id)

  const { searchParams, remove: removeSearchParams } = useSearchParams<{ reFetchAppointmentProducts?: string }>()

  useEffect(() => {
    ; (async () => {
      if (searchParams.reFetchAppointmentProducts) {
        removeSearchParams.current('reFetchAppointmentProducts')
        reFetch()
        reFetchProducts()
      }
    })()
  }, [reFetch, reFetchProducts, removeSearchParams, searchParams.reFetchAppointmentProducts])

  const goToAddProductPage = useCallback(() => {
    const pathname = localURLMaker(AppointmentRoutes.ADD_PRODUCT, { id })
    navigate(pathname, { withBackground: true })
  }, [navigate, id])

  const goToProductDetailsPage = useCallback(
    (productId: number) => {
      const pathname = localURLMaker(AppointmentRoutes.PRODUCT_DETAILS, { id: productId })
      navigate(pathname, { withBackground: true })
    },
    [navigate],
  )

  const goToPrintHash = useCallback(() => {
    if (!data?.printHash) {
      message.info('Keçid linki mövcud deyil.')
      return
    }
    const url = urlMaker(`/visits/${id}/print`, { hash: data.printHash })

    window.open(url, '_blank')
  }, [data?.printHash, id])

  const extraNode = useMemo(() => {
    return (
      <Space size={8}>
        {can(67) && (
          <Button size='small' type='link' onClick={edit} icon={<Icons.EditOutlined />}>
            Düzəliş et
          </Button>
        )}
        {can(69) && (
          <Button size='small' type='link' onClick={remove} danger={true} icon={<Icons.DeleteOutlined />}>
            Sil
          </Button>
        )}
      </Space>
    )
  }, [can, edit, remove])

  const titleNode = useMemo(() => {
    return (
      <Space size={8}>
        <Icons.LeftCircleOutlined onClick={() => navigate(-1)} />
        <span>
          #{id} nömrəli qəbul {!!data && `(${data.customer.fullName})`}
        </span>
      </Space>
    )
  }, [data, navigate, id])

  return (
    <PageContent extra={extraNode} title={titleNode}>
      <Spin spinning={loading}>
        <div>
          {data && (
            <Fragment>
              <Space size={8}>
                <Button onClick={print.check} style={{ marginBottom: 12 }} icon={<Icons.PrinterOutlined />}>
                  Kassa çeki
                </Button>
                {can(68) && (
                  <Dropdown
                    menu={{
                      items: statusList.data.map((status) => ({
                        key: status.id,
                        label: status.name,
                        onClick: () => updateStatus(status.id),
                      })),
                    }}
                  >
                    <Button style={{ marginBottom: 12 }} icon={<Icons.AppstoreOutlined />}>
                      Statusu dəyiş
                    </Button>
                  </Dropdown>
                )}

                <Button disabled={!data?.printHash} onClick={goToPrintHash} style={{ marginBottom: 12 }} icon={<Icons.PrinterOutlined />}>
                  Qəbul sənədini V2
                </Button>

                <Button onClick={print.document} style={{ marginBottom: 12 }} icon={<Icons.PrinterOutlined />}>
                  Qəbul sənədini
                </Button>

                {can(132) && (
                  <Button style={{ marginBottom: 12 }} onClick={goToAddProductPage} icon={<Icons.FileAddOutlined />}>
                    Sərfiyyat Əlavə Et
                  </Button>
                )}
              </Space>
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Card bodyStyle={{ padding: 0 }} type='inner' size='small' title='Müştəri məlumatları'>
                    <Descriptions size='small' column={1} bordered={true}>
                      <Descriptions.Item label='Kod'>{data.customer.id}</Descriptions.Item>
                      <Descriptions.Item label='Ad'>{data.customer.fullName}</Descriptions.Item>
                      <Descriptions.Item label='Cins'>{data.customer.gender === 'male' ? 'Kişi' : 'Qadın'}</Descriptions.Item>
                      <Descriptions.Item label='Sığorta nömrəsi'>{data.customer.insuranceNumber}</Descriptions.Item>
                      <Descriptions.Item label='Franşiza'>{data.customer.franchise}</Descriptions.Item>
                      <Descriptions.Item label='Milliyət'>{data.customer.nationality}</Descriptions.Item>
                      <Descriptions.Item label='FİN kod'>{data.customer.passport.secret}</Descriptions.Item>
                      <Descriptions.Item label='Mobil nömrə'>{data.customer.phoneNumber}</Descriptions.Item>
                      <Descriptions.Item label='Status'>{data.customer.status.name}</Descriptions.Item>
                      <Descriptions.Item label='Şərh'>{data.customer.description || 'Qeyd olunmayıb'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card bodyStyle={{ padding: 0 }} type='inner' size='small' title='Qəbul məlumatları'>
                    <Descriptions size='small' bordered={true} colon={true} column={1}>
                      <Descriptions.Item label='Kod'>{data.id}</Descriptions.Item>
                      <Descriptions.Item label='Tip'>{data.type === 'outpatient' ? 'Ambulator' : 'Göndəriş'}</Descriptions.Item>
                      {!!data.sentBy && (
                        <Descriptions.Item label='Həkim'>
                          {data.sentBy?.name} ({data.sentBy?.clinic?.name})
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label='Nağd'>{data.payment.cash} AZN</Descriptions.Item>
                      <Descriptions.Item label='Bank'>{data.payment.card} AZN</Descriptions.Item>
                      <Descriptions.Item label='ƏDV-siz məbləğ'>{data.payment.base_price} AZN</Descriptions.Item>
                      <Descriptions.Item label='ƏDV məbləği'>{data.payment.vat_amount} AZN</Descriptions.Item>
                      <Descriptions.Item label='Status'>{data.status.name}</Descriptions.Item>
                      <Descriptions.Item label='Qəbul tarixi'>{data.scheduledAt}</Descriptions.Item>
                      <Descriptions.Item label='Yaradılma tarixi'>{data.createdAt}</Descriptions.Item>
                      <Descriptions.Item label='Şərh'>{data.description || 'Qeyd olunmayıb'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                {data.insurance.name && (
                  <Col xs={24} sm={12}>
                    <Card bodyStyle={{ padding: 0 }} type='inner' size='small' title='Sığorta məlumatları'>
                      <Descriptions size='small' bordered={true} colon={true} column={1}>
                        <Descriptions.Item label='Sığorta şirkətinin adı'>{data.insurance?.name}</Descriptions.Item>
                        <Descriptions.Item label='Sığorta şirkətinin endirim faizi'>{data.insurance?.percent}</Descriptions.Item>
                        <Descriptions.Item label='Sığorta şirkətinin endirim məbləği'>{data.insurance?.insuranceDiscount}</Descriptions.Item>
                        <Descriptions.Item label='Sığorta şirkətinin şərhi'>{data.insurance?.descr}</Descriptions.Item>
                        <Descriptions.Item label='Sığorta şirkətinin ödəyəcəyi məbləğ'>{data.insurance?.insuranceAmount}</Descriptions.Item>
                        <Descriptions.Item label='Müştərinin ödəyəcəyi məbləğ(franşizası)'>{data.insurance?.clientFranchise}</Descriptions.Item>
                        <Descriptions.Item label='Sığorta şirkətinin endirimi'>{data.insurance?.insuranceVat}</Descriptions.Item>
                        <Descriptions.Item label='ƏDV'>{data.insurance?.vat}</Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                )}
              </Row>
              <Card bodyStyle={{ padding: 0 }} type='inner' style={{ marginTop: 12 }} size='small' title='Xidmətlər'>
                <Table pagination={{ hideOnSinglePage: true }} dataSource={data.services} rowKey='id' size='small' bordered={true}>
                  <Table.Column dataIndex='id' title='Kod' />
                  <Table.Column dataIndex={['reference', 'name']} title='Xidmət' />
                  <Table.Column dataIndex={['reference', 'price']} title='Xidmətin qiyməti' />
                  <Table.Column dataIndex='quantity' title='Say' />
                  <Table.Column dataIndex='discount' title='Endirim' />
                  <Table.Column dataIndex='totalPrice' title='Ümumi qiymət' />
                  <Table.Column dataIndex={['doctor', 'name']} title='Həkim' />
                </Table>
              </Card>
              <Card bodyStyle={{ padding: 0 }} type='inner' style={{ marginTop: 12 }} size='small' title='Sərfiyyatlar'>
                <Table pagination={{ hideOnSinglePage: true, pageSize: 25 }} dataSource={products} rowKey='id' size='small' bordered={true}>
                  <Table.Column
                    key='actions'
                    width={48}
                    render={(_, record: AppointmentProduct) => {
                      return (
                        <StopPropagation>
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: 'details',
                                  label: 'Ətraflı',
                                  icon: <Icons.FileTextOutlined />,
                                  onClick: () => goToProductDetailsPage(record.id),
                                },
                              ],
                            }}
                          >
                            <Button size='small' icon={<Icons.MoreOutlined />} />
                          </Dropdown>
                        </StopPropagation>
                      )
                    }}
                  />
                  <Table.Column dataIndex={['product', 'name']} title='Məhsul adı' />
                  <Table.Column dataIndex='quantity' title='Say' />
                  <Table.Column dataIndex={['warehouse', 'name']} title='Anbar' />
                  <Table.Column dataIndex={['doctor', 'name']} title='Həkim' />
                  <Table.Column dataIndex='productPrice' title='Məhsulun qiyməti' />
                  <Table.Column dataIndex='totalPrice' title='Cəmi məbləğ' />
                  <Table.Column dataIndex='createdAt' title='Xərclənmə tarixi' />
                </Table>
              </Card>
            </Fragment>
          )}
        </div>
      </Spin>
    </PageContent>
  )
}
